import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layers, Loader2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { BulkRow, sumSal } from './BulkRow';

const API = process.env.REACT_APP_BACKEND_URL;
const ALL = '__all__';

const StaffPicker = ({ emps, picked, onPick }) => {
  const [dept, setDept] = useState(ALL);
  const [q, setQ] = useState('');
  const depts = useMemo(() => [...new Set(emps.map(e => e.department).filter(Boolean))].sort(), [emps]);
  const list = emps.filter(e => (dept === ALL || e.department === dept) && !picked.includes(e.id)
    && `${e.name} ${e.employee_code}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="bulk-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All departments</SelectItem>{depts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Input className="h-8 text-xs" placeholder="Search name or code" value={q} onChange={e => setQ(e.target.value)} data-testid="bulk-search" />
        <Button type="button" size="sm" variant="outline" className="h-8 text-xs shrink-0" disabled={!list.length}
          onClick={() => onPick(list.map(e => e.id))} data-testid="bulk-add-all">Add all {list.length}</Button>
      </div>
      <div className="max-h-32 overflow-y-auto border rounded-md divide-y">
        {list.slice(0, 80).map(e => (
          <button type="button" key={e.id} onClick={() => onPick([e.id])} className="w-full text-left px-2 py-1 text-xs hover:bg-violet-50" data-testid={`bulk-pick-${e.employee_code}`}>
            {e.name} <span className="text-gray-400">· {e.employee_code} · {e.department} · {sumSal(e.salary).toLocaleString()}</span>
          </button>
        ))}
        {!list.length && <p className="text-xs text-gray-400 p-2">No more staff.</p>}
      </div>
    </div>
  );
};

export const BulkSalaryRevision = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('raise');
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState({});
  const [eff, setEff] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [sigs, setSigs] = useState([]);
  const [sig, setSig] = useState('0');

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/employees`).then(r => setEmps((r.data || []).filter(e => e.is_active !== false && !['Resigned', 'Terminated'].includes(e.employment_status)))).catch(() => {});
    axios.get(`${API}/api/employees/promotion/signatories`).then(r => setSigs(r.data || [])).catch(() => {});
  }, [open]);

  const reset = () => { setRows({}); setSaved(null); setReason(''); setEff(''); };
  const byId = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);
  const ids = Object.keys(rows);

  const submit = async () => {
    setBusy(true);
    try {
      const items = ids.map(id => ({ employee_id: id, new_position: rows[id].new_position || null,
        [mode === 'raise' ? 'new_salary' : 'previous_salary']: Object.fromEntries(Object.entries(rows[id].salary || {}).map(([k, v]) => [k, parseFloat(v) || 0])) }));
      const r = await axios.post(`${API}/api/salary-bulk/revise`, { mode, effective_date: eff, reason, items });
      setSaved(r.data);
      toast({ title: `${r.data.saved.length} revision(s) saved`, description: r.data.errors.length ? `${r.data.errors.length} skipped` : '' });
      onDone?.();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const letters = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/salary-bulk/letters`, { items: saved.saved.map(s => ({ employee_id: s.employee_id, promotion_id: s.promotion_id })), signatory_index: Number(sig) }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: 'Salary_Revision_Letters.zip' }).click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Letters download failed', description: 'Please try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const ready = ids.length && eff && ids.every(id => sumSal(rows[id].salary) > 0);
  return (
    <>
      <Button size="sm" onClick={() => { reset(); setOpen(true); }} className="gap-1.5 bg-violet-600 hover:bg-violet-700" data-testid="bulk-revision-btn">
        <Layers className="h-4 w-4" /> Bulk salary revision
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="bulk-revision-dialog">
          <DialogHeader><DialogTitle>Bulk salary revision</DialogTitle></DialogHeader>
          {saved ? (
            <div className="space-y-3" data-testid="bulk-result">
              <p className="text-sm">Saved <b>{saved.saved.length}</b> revision(s).{saved.errors.length ? ` Skipped ${saved.errors.length}: ${saved.errors.map(e => `${e.name || e.employee_id} (${e.error})`).join(', ')}` : ''}</p>
              {saved.saved.length > 0 && (
                <div className="flex items-end gap-2">
                  <div><Label className="text-xs">Signed by</Label>
                    <Select value={sig} onValueChange={setSig}><SelectTrigger className="w-64 h-9" data-testid="bulk-signatory"><SelectValue /></SelectTrigger>
                      <SelectContent>{sigs.map((s, i) => <SelectItem key={i} value={String(i)}>{s.name} — {s.designation}</SelectItem>)}</SelectContent></Select></div>
                  <Button onClick={letters} disabled={busy} data-testid="bulk-letters-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Download all letters (ZIP)</Button>
                </div>
              )}
              <Button variant="outline" onClick={reset} data-testid="bulk-new-btn">Start another</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                {[['raise', 'New raise'], ['record', 'Record a past raise (enter old salary)']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => { setMode(k); setRows(r => Object.fromEntries(Object.keys(r).map(id => [id, {}]))); }}
                    className={`px-3 py-1.5 rounded-md text-sm ${mode === k ? 'bg-white shadow-sm font-medium' : 'text-gray-500'}`} data-testid={`bulk-mode-${k}`}>{l}</button>
                ))}
              </div>
              <p className="text-xs text-gray-500">{mode === 'raise'
                ? 'Type each new salary. The current salary is kept as the old salary in Promotion History. Future dates apply automatically on that day.'
                : "For raises already typed into profiles: enter each person's OLD salary. Their profile is not changed; the revision is just recorded so comparisons and letters are correct."}</p>
              <StaffPicker emps={emps} picked={ids} onPick={add => setRows(r => ({ ...r, ...Object.fromEntries(add.map(id => [id, {}])) }))} />
              {ids.length > 0 && (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase text-gray-500"><tr>
                      <th className="text-left px-2 py-2">Staff</th>{mode === 'raise' && <th className="text-left px-2 py-2">New position (optional)</th>}
                      <th className="text-left px-2 py-2">{mode === 'raise' ? 'New total' : 'Old total'}</th>
                      <th className="text-left px-1 py-2">Basic</th><th className="text-left px-1 py-2">Housing</th><th className="text-left px-1 py-2">Transport</th><th className="text-left px-1 py-2">Other</th>
                      <th className="text-left px-2 py-2">Change</th><th /></tr></thead>
                    <tbody className="divide-y">
                      {ids.map(id => byId[id] && <BulkRow key={id} emp={byId[id]} row={rows[id]} mode={mode}
                        onChange={v => setRows(r => ({ ...r, [id]: v }))} onRemove={() => setRows(r => { const n = { ...r }; delete n[id]; return n; })} />)}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Effective date *</Label><Input type="date" value={eff} onChange={e => setEff(e.target.value)} data-testid="bulk-effective-date" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Reason</Label><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Annual increment 2026" data-testid="bulk-reason" /></div>
              </div>
              <div className="flex justify-end">
                <Button disabled={!ready || busy} onClick={submit} data-testid="bulk-save-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save {ids.length} revision(s)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
